<template>
  <AuthLogin
    :header="showMfa ? '' : header"
    :subheader="showMfa ? '' : subheader"
  >
    <form class="min-w-full" @submit.prevent="loginUser">
      <BaseBanner
        v-if="!showMfa && showErrorMessage"
        id="login-errors-banner"
        :class="['mb-6', showMfa]"
        icon="alertTriangle"
        class="text-left"
        data-qa="error-bar"
        :message="errorMessage"
        variant="error"
        :show-close="false"
      />
      <div v-if="showMfa && !isOtpLoading">
        <MfaForm
          :phone="phone"
          :expiration-timer="remainingTime"
          @cancelChallenge="showMfa = false"
          @otpSubmit="loginUser($event)"
        >
          <template #errors>
            <BaseBanner
              v-if="showErrorMessage"
              id="mfa-errors-banner"
              :class="['mb-6', showMfa]"
              icon="alertTriangle"
              class="text-left"
              data-qa="error-bar"
              :message="errorMessage"
              variant="error"
              :show-close="false"
            />
          </template>
        </MfaForm>
        <TypeBody
          variant="link-x-small-tight"
          weight="strong"
          class="cursor-pointer flex items-center justify-center"
          @click="handleNewCode"
        >
          Text me a new code
        </TypeBody>
      </div>

      <div v-show="!showMfa">
        <TextInput
          id="email-input"
          v-model="form.email"
          data-cy="email-input"
          type="email"
          label="Email"
          autocomplete="username"
          required
        />

        <PasswordInput
          id="password-input"
          v-model="form.password"
          data-cy="password-input"
          autocomplete="current-password"
          :validate="false"
        />

        <BaseButton
          id="login"
          data-cy="login-button"
          size="large"
          variant="secondary"
          :loading="loading"
          :disabled="loading"
          class="w-full"
        >
          <LoadingButtonLabel :loading="loading"> Log In </LoadingButtonLabel>
        </BaseButton>
      </div>
    </form>

    <div v-if="!isOtpLoading && !showMfa" class="options_wrapper">
      <nuxt-link to="forgot-pass" append>
        <span>Forgot Password?</span>
      </nuxt-link>
    </div>

    <div v-if="isOtpLoading" class="flex flex-col justify-center">
      <TypeBody
        id="loading-text"
        variant="link-large-tight"
        weight="strong"
        tag="div"
      >
        Signing In...
      </TypeBody>
      <AnnularThrobber id="sign-in-loading-throbber" class="self-center my-4" />
    </div>
  </AuthLogin>
</template>

<script>
import { auth } from '@/constants/Store';
import {
  AnnularThrobber,
  BaseBanner,
  BaseButton,
  TextInput,
  TypeBody,
} from '@bambeehr/pollen';
import {
  defineComponent,
  ref,
  watch,
  getCurrentInstance,
  useRoute,
  computed,
} from '@nuxtjs/composition-api';
import { mapGetters } from 'vuex';

import MfaForm from '@/components/settings/MfaForm';
import bam from '@/lib/bam';
import { AuthMfaType } from '@/gql/generated';
import useMfa from '@/modules/Settings/hooks/useMfa';
import PasswordInput from '@/shared/components/form-components/password-input.vue';
import AuthLogin from '@/shared/components/utility/auth-login.vue';
import startCountdown, {
  timeRemaining,
} from '@/modules/Settings/utils/startCountdown';
import MFA_TIMER from '@/modules/Settings/constants/Mfa';
import LoadingButtonLabel from '@/components/LoadingButtonLabel';
import useCompanyStore from '@/store/companies';

const ErrorMessages = {
  UNAUTHENTICATED: 'Your email or password is incorrect',
  OTHER: 'Our server is not responding, please try again later',
  MFA: 'The code you entered is incorrect',
  CHALLENGE_NOT_STARTED: 'A challenge for this device has not started',
  EXPIRED_CODE: 'The code you entered is incorrect or expired',
};
const ErrorCodes = {
  LOGIN_CHALLENGE_REQUIRED: 412,
  INCORRECT_CREDENTIAL: 401,
  NOT_FOUND: 404,
  FORBIDDEN: 403,
  REDIRECT: 302,
};

const SSO = Object.freeze({
  DEPUTY: 'deputy',
});

export default defineComponent({
  name: 'LoginPage',
  components: {
    AnnularThrobber,
    AuthLogin,
    BaseBanner,
    BaseButton,
    LoadingButtonLabel,
    MfaForm,
    PasswordInput,
    TextInput,
    TypeBody,
  },
  setup() {
    const { resendChallenge } = useMfa();
    const remainingTime = ref(MFA_TIMER);
    const currentTimer = ref(null);
    const { $toast: toast } = getCurrentInstance().proxy;
    const route = useRoute();

    const ssoClient = computed(() => route.value.query.sso);
    const ssoRedirect = computed(() => route.value.query.redirect);
    const ssoState = computed(() =>
      route.value.query.state ? `&state=${route.value.query.state}` : ''
    );

    const isSSO = computed(
      () => !!ssoClient.value && Object.values(SSO).includes(ssoClient.value)
    );

    const handleTimerStart = (time) => {
      startCountdown(time);
    };

    watch(timeRemaining, (newVal) => {
      remainingTime.value = newVal;
    });

    const handleResendCode = (nonce, reason, type) => {
      const { loading, errors } = resendChallenge(nonce, reason, type);
      watch(
        loading,
        () => {
          if (!loading.value) {
            if (!errors.value.hasErrors) {
              toast.open({
                type: 'is-success',
                message: `A new code has been sent`,
              });
              handleTimerStart(MFA_TIMER);
            } else {
              toast.open({
                type: 'is-danger',
                message: `Code was not sent, try again later`,
              });
            }
          }
        },
        { immediate: true }
      );

      return errors.value;
    };

    return {
      resendChallenge,
      startCountdown,
      remainingTime,
      handleTimerStart,
      currentTimer,
      handleResendCode,
      isSSO,
      ssoClient,
      ssoRedirect,
      ssoState,
    };
  },
  data() {
    return {
      form: {
        email: '',
        password: '',
        otpCode: '',
        nonce: '',
      },
      loading: false,
      showMfa: false,
      mfaReason: '',
      isOtpLoading: false,
      showErrorMessage: false,
      resendStatus: '',
      errorMessage: ErrorMessages.UNAUTHENTICATED,
    };
  },

  computed: {
    ...mapGetters({
      isOwner: auth.Getters.IS_OWNER,
    }),
    redir() {
      return this.$route.query.redir || null;
    },
    formVal() {
      // find the form with document id 'otp-form' and return the values
      return window.document.getElementById('otp-form');
    },
    header() {
      return this.showMfa ? '' : 'Welcome to Bambee';
    },
    subheader() {
      return this.showMfa ? '' : 'Enter your email and password to log in';
    },
  },
  mounted() {
    if (this.current_user) {
      this.$router.push('/');
    }
  },

  methods: {
    handleNewCode() {
      this.handleResendCode(this.form.nonce, this.mfaReason, AuthMfaType.Sms);
    },
    getOtpFormValues(formVal) {
      const otpCode = [
        formVal[0].value,
        formVal[1].value,
        formVal[2].value,
        formVal[3].value,
        formVal[4].value,
        formVal[5].value,
      ].join('');

      return otpCode;
    },
    async loginUser(data) {
      // If there's a pending login request, exit
      // Prevents duplicate dispatches to login action
      if (this.loading) {
        return;
      }

      if (this.showMfa) {
        this.isOtpLoading = true;
      }
      this.loading = true;
      this.showErrorMessage = false;
      let user;

      if (data?.id === 'otp-form') {
        this.form.otpCode = this.getOtpFormValues(data);
      }
      try {
        if (this.isSSO) {
          const redirect = await this.$store.dispatch(auth.Actions.SSO_LOGIN, {
            ...this.form,
            clientId: this.ssoClient,
            redirect: this.ssoRedirect,
          });
          window.location.href = `${redirect}${this.ssoState}`;
        } else {
          user = await this.$store.dispatch(auth.Actions.LOGIN, this.form);
        }
      } catch (err) {
        let isMfa;
        this.loading = false; // in catch only because should be redirected in all other cases
        if (this.isOtpLoading) {
          isMfa = true;
          this.isOtpLoading = false;
        }
        switch (err.response?.status) {
          case ErrorCodes.NOT_FOUND:
            if (isMfa) {
              this.errorMessage = ErrorMessages.EXPIRED_CODE;
            }
            this.showErrorMessage = true;
            break;
          case ErrorCodes.INCORRECT_CREDENTIAL:
            this.errorMessage = ErrorMessages.UNAUTHENTICATED;
            this.showErrorMessage = true;
            break;
          case ErrorCodes.LOGIN_CHALLENGE_REQUIRED:
            this.handleTimerStart(MFA_TIMER);
            this.showErrorMessage = false;
            this.phone = err.response?.data?.metadata?.phoneLast4;
            this.form.nonce = err.response?.data?.nonce;
            this.showMfa = true;
            this.mfaReason = err.response?.data?.reason;
            break;
          case ErrorCodes.FORBIDDEN:
            this.showErrorMessage = true;
            this.isOtpLoading = false;
            this.errorMessage = err.response?.data?.message;
            break;
          default:
            this.showErrorMessage = true;
            this.errorMessage = ErrorMessages.OTHER;
        }
      }
      if (user) {
        bam.identify(user);
        if (this.isOwner) {
          // // This will fetch milestones and redirect OWNER to Onboarding Wizard
          await useCompanyStore().getOnboardingMilestones();
        }
        this.$router.push(this.redir || '/');
      }
    },
  },
});
</script>
<style lang="scss" scoped>
@import '~assets/css/shared.scss';
$dark-3: #b9b8ba;

p {
  color: $black;
  font-size: remy(16);
  line-height: remy(19);
}

span {
  color: #68478d;
  font-size: remy(12);
  line-height: remy(14);
}

form {
  margin-top: 24px;
}

.password-input {
  margin-top: 16px;
}

.button {
  display: block;
  margin-top: 1rem;
}

.options_wrapper {
  align-items: center;
  display: flex;
  justify-content: space-between;

  a span {
    color: $dark-3;
    transition: hover 200ms ease-in-out;

    &:hover {
      color: $primary;
      opacity: 0.8;
    }
  }
}
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  /* IE10+ CSS styles go here */

  .field {
    margin-bottom: 3rem !important;
  }

  .button {
    margin-top: 3rem !important;
  }

  .options_wrapper {
    margin-top: 1rem;
  }
}
</style>
